import { useState } from "react";
import { Box, IconButton, TextField, Tooltip, Alert } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { useChatRail } from "./ChatRailProvider";
import { useCreateConversation, conversationTabsKey } from "../hooks/useConversationTabs";
import { useAutoAttach, type AttachmentKind } from "../hooks/useAutoAttach";
import { AttachmentChip } from "./AttachmentChip";

interface InvokeResult {
  run_id: string;
  conversation_id: string;
}

interface MessageAttachment {
  kind: AttachmentKind;
  id: string;
}

async function invokeAgent(args: {
  agent_id: string;
  conversation_id?: string;
  message: string;
  attachments?: MessageAttachment[];
}): Promise<InvokeResult> {
  const { data: sessionRes } = await supabase.auth.getSession();
  const accessToken = sessionRes.session?.access_token;
  if (!accessToken) throw new Error("Not signed in.");
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const clientRequestId = crypto.randomUUID();
  const res = await fetch(`${supabaseUrl}/functions/v1/manna-agent-invoke`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      agent_id: args.agent_id,
      conversation_id: args.conversation_id,
      message: {
        role: "user",
        content: args.message,
        ...(args.attachments && args.attachments.length > 0
          ? { metadata: { attachments: args.attachments } }
          : {}),
      },
      client_request_id: clientRequestId,
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = body?.error?.code ?? "internal_error";
    const msg = body?.error?.message ?? `HTTP ${res.status}`;
    throw new Error(`${code}: ${msg}`);
  }
  return body as InvokeResult;
}

export function Composer({
  agentId,
  conversationId,
}: {
  agentId: string;
  conversationId: string | null;
}) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const qc = useQueryClient();
  const { setActiveConvoId } = useChatRail();
  const create = useCreateConversation(agentId);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { attachment, dismiss } = useAutoAttach();

  const send = useMutation({
    mutationFn: async (text: string) => {
      let convoId = conversationId;
      if (!convoId) {
        convoId = await create.mutateAsync();
        setActiveConvoId(convoId);
      }
      const attachments: MessageAttachment[] = attachment
        ? [{ kind: attachment.kind, id: attachment.id }]
        : [];
      return invokeAgent({
        agent_id: agentId,
        conversation_id: convoId,
        message: text,
        attachments,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["conversation-messages", res.conversation_id] });
      qc.invalidateQueries({ queryKey: conversationTabsKey(agentId) });
    },
  });

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setError(null);
    try {
      await send.mutateAsync(text);
      setDraft("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send.");
    }
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${T.hair}`,
        bgcolor: T.rail,
        px: 1.5,
        py: 1,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {attachment && (
        <AttachmentChip kind={attachment.kind} label={attachment.label} onDismiss={dismiss} />
      )}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Message…"
          fullWidth
          multiline
          maxRows={6}
          variant="standard"
          InputProps={{ disableUnderline: true, sx: { fontSize: T.fs_body } }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!send.isPending) handleSend();
            }
          }}
          disabled={send.isPending}
          sx={{
            border: `1px solid ${T.hair2}`,
            borderRadius: `${T.r_md}px`,
            px: 1,
            py: 0.5,
            "&:focus-within": { borderColor: T.accentBorder },
          }}
        />
        <Tooltip title="Voice input — coming soon">
          <span>
            <IconButton disabled size="small" aria-label="Voice input (coming soon)">
              <MicIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!draft.trim() || send.isPending}
              size="small"
              sx={{ color: T.accent }}
              aria-label="Send"
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
